import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import {
  MODULE_CATALOG,
  isModuleEnabled,
  type EnabledModules,
  type ModuleKey,
} from '@os/contracts/shared'
import { MODULE_UI_META } from '../../utils/moduleUiMeta'
import { adminApi, departmentApi, serverApi, type ModuleAccessScope } from '../../services/api'
import { PrincipalPicker, type Principal } from '../common/PrincipalPicker'

interface Props {
  serverId: string
  serverName: string
  onClose: () => void
}

interface MiniEntry { id: string; name: string }

const PUBLIC_SCOPE: ModuleAccessScope = { visibility: 'public', departmentIds: [], userIds: [] }

// Global-admin drawer for editing a server's module subscription.
// Loads enabledModules + per-module visibility scope on open, sends a
// diff on save (only the keys that changed for on/off, plus a per-module
// access PATCH for any module whose visibility scope changed). The socket
// broadcasts (server:modules:updated / server:moduleAccess:updated) handle
// propagation to every connected client — no parent refetch needed.
export default function ServerModulesModal({ serverId, serverName, onClose }: Props) {
  const [enabled, setEnabled] = useState<EnabledModules>({})
  const [initial, setInitial] = useState<EnabledModules>({})
  // Per-module visibility scope, keyed by module key. Seeded from the
  // server's moduleAccess; missing keys default to `public`.
  const [access, setAccess] = useState<Record<string, ModuleAccessScope>>({})
  const [accessInitial, setAccessInitial] = useState<Record<string, ModuleAccessScope>>({})
  const [departments, setDepartments] = useState<MiniEntry[]>([])
  const [members, setMembers] = useState<MiniEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    adminApi
      .getServerModules(serverId)
      .then((res) => {
        if (cancelled) return
        setEnabled(res.data.enabledModules ?? {})
        setInitial(res.data.enabledModules ?? {})
        setAccess(res.data.moduleAccess ?? {})
        setAccessInitial(res.data.moduleAccess ?? {})
      })
      .catch((err: any) => {
        if (cancelled) return
        setError(err?.response?.data?.error ?? 'Failed to load modules')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [serverId])

  // Departments + members for the restricted-visibility picker. Lazy —
  // this admin drawer is only opened by privileged users. Fail-soft to
  // empty lists so a roster blip never breaks the on/off toggles.
  useEffect(() => {
    let cancelled = false
    departmentApi
      .list(serverId)
      .then((res: any) => {
        if (cancelled) return
        const list = Array.isArray(res.data) ? res.data : (res.data?.departments ?? [])
        setDepartments(list.map((d: any) => ({ id: d.id, name: d.name })))
      })
      .catch(() => {})
    serverApi
      .members(serverId)
      .then((res: any) => {
        if (cancelled) return
        setMembers(
          (res.data ?? [])
            .map((m: any) => {
              const u = m.user ?? m
              if (!u?.id) return null
              const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.username || u.email || u.id
              return { id: u.id, name }
            })
            .filter(Boolean) as MiniEntry[],
        )
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [serverId])

  const toggle = (key: ModuleKey) => {
    // The catalog helper owns each module's default. Legacy missing keys are
    // on; managed Device Management is off until explicitly enabled.
    setEnabled((prev) => ({ ...prev, [key]: !isModuleEnabled(prev, key) }))
  }

  const scopeFor = (key: string): ModuleAccessScope => access[key] ?? PUBLIC_SCOPE

  const setVisibility = (key: string, visibility: 'public' | 'restricted' | 'admins') => {
    setAccess((prev) => {
      const cur = prev[key] ?? PUBLIC_SCOPE
      return { ...prev, [key]: { ...cur, visibility } }
    })
  }

  const addPrincipal = (key: string, p: Principal) => {
    setAccess((prev) => {
      const cur = prev[key] ?? PUBLIC_SCOPE
      if (p.type === 'department') {
        if (cur.departmentIds.includes(p.id)) return prev
        return { ...prev, [key]: { ...cur, departmentIds: [...cur.departmentIds, p.id] } }
      }
      if (cur.userIds.includes(p.id)) return prev
      return { ...prev, [key]: { ...cur, userIds: [...cur.userIds, p.id] } }
    })
  }

  const removePrincipal = (key: string, p: Principal) => {
    setAccess((prev) => {
      const cur = prev[key] ?? PUBLIC_SCOPE
      if (p.type === 'department') {
        return { ...prev, [key]: { ...cur, departmentIds: cur.departmentIds.filter((d) => d !== p.id) } }
      }
      return { ...prev, [key]: { ...cur, userIds: cur.userIds.filter((u) => u !== p.id) } }
    })
  }

  // Map a scope's ids back into Principal chips for the picker, resolving
  // friendly names from the loaded department / member lists.
  const selectedFor = (key: string): Principal[] => {
    const cur = scopeFor(key)
    const deptName = (id: string) => departments.find((d) => d.id === id)?.name ?? id
    const userName = (id: string) => members.find((u) => u.id === id)?.name ?? id
    return [
      ...cur.departmentIds.map((id): Principal => ({ type: 'department', id, name: deptName(id) })),
      ...cur.userIds.map((id): Principal => ({ type: 'user', id, name: userName(id) })),
    ]
  }

  const diff = (): Partial<Record<string, boolean>> => {
    const out: Partial<Record<string, boolean>> = {}
    for (const m of MODULE_CATALOG) {
      const before = isModuleEnabled(initial, m.key)
      const after = isModuleEnabled(enabled, m.key)
      if (before !== after) out[m.key] = after
    }
    return out
  }

  const accessChanged = (key: string): boolean => {
    const before = accessInitial[key] ?? PUBLIC_SCOPE
    const after = scopeFor(key)
    return JSON.stringify(before) !== JSON.stringify(after)
  }

  const save = async () => {
    const patch = diff()
    const changedAccessKeys = MODULE_CATALOG
      .map((m) => m.key)
      .filter((key) => accessChanged(key))
    if (Object.keys(patch).length === 0 && changedAccessKeys.length === 0) { onClose(); return }
    setSaving(true)
    setError(null)
    try {
      if (Object.keys(patch).length > 0) {
        await adminApi.updateServerModules(serverId, patch)
      }
      for (const key of changedAccessKeys) {
        await adminApi.updateModuleAccess(serverId, key, scopeFor(key))
      }
      onClose()
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-[480px] max-h-[80vh] bg-card border border-border rounded-lg shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Modules</h2>
            <p className="text-xs text-muted-foreground">{serverName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {loading && (
            <div className="text-muted-foreground text-sm py-6 text-center">Loading…</div>
          )}
          {error && (
            <div className="text-destructive text-sm py-2 px-3">{error}</div>
          )}
          {!loading && !error && MODULE_CATALOG.map((m) => {
            const meta = MODULE_UI_META[m.key]
            const Icon = meta.icon
            const on = isModuleEnabled(enabled, m.key)
            const scope = scopeFor(m.key)
            const vis = scope.visibility
            return (
              <div key={m.key} data-testid={`module-row-${m.key}`} className="rounded">
                <button
                  type="button"
                  onClick={() => toggle(m.key)}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-accent/40 transition text-left"
                >
                  <Icon size={18} className="text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-foreground">{m.label}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      {m.status}
                    </div>
                  </div>
                  <span
                    className={`relative shrink-0 w-9 h-5 rounded-full transition ${
                      on ? 'bg-primary' : 'bg-muted'
                    }`}
                    aria-hidden
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-background transition ${
                        on ? 'left-4' : 'left-0.5'
                      }`}
                    />
                  </span>
                </button>

                {/* Visibility editor — only meaningful while the module is
                    enabled. Public = every member; Restricted = the listed
                    departments / people only; Admins only = server + global
                    admins (no per-member list). */}
                {on && (
                  <div className="px-3 pb-2 pt-1 space-y-2">
                    <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
                      <button
                        type="button"
                        onClick={() => setVisibility(m.key, 'public')}
                        className={`px-2.5 py-1 transition ${
                          vis === 'public'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-card text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        Public
                      </button>
                      <button
                        type="button"
                        onClick={() => setVisibility(m.key, 'restricted')}
                        className={`px-2.5 py-1 transition border-l border-border ${
                          vis === 'restricted'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-card text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        Restricted
                      </button>
                      <button
                        type="button"
                        onClick={() => setVisibility(m.key, 'admins')}
                        className={`px-2.5 py-1 transition border-l border-border ${
                          vis === 'admins'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-card text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        Admins only
                      </button>
                    </div>

                    {vis === 'restricted' && (
                      <PrincipalPicker
                        users={members}
                        departments={departments}
                        selected={selectedFor(m.key)}
                        onAdd={(p) => addPrincipal(m.key, p)}
                        onRemove={(p) => removePrincipal(m.key, p)}
                      />
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-border">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || loading}
            className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50 transition"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
